import React, { useCallback, useMemo, useState } from 'react';
import {
  ProfileHeader,
  InfoCard,
  NotificationPreferences,
  SecuritySettings,
  SettingsSection,
  LogoutButton,
} from './components';
import {
  DriverProfile as DriverProfileType,
  PersonalInfo,
  ContactInfo,
  NotificationPreference,
  defaultNotificationPreferences,
  defaultSecurityOptions,
  defaultSettingsItems,
} from './profileTypes';
import { useAuth, isUserActive } from '../../../contexts/AuthContext';
import { ChangePasswordModal } from '../../../components/ChangePasswordModal';
import styles from './DriverProfile.module.css';

const DriverProfile: React.FC = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreference[]>(defaultNotificationPreferences);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleTogglePreference = useCallback((id: string) => {
    setPreferences((prev) => prev.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p)));
  }, []);

  const handleSecurityClick = useCallback((id: string) => {
    if (id === 'change-password') setShowPasswordModal(true);
  }, []);

  const profile: DriverProfileType = useMemo(() => ({
    id: user?.id ?? 'N/A',
    staffName: user?.full_name ?? 'N/A',
    role: user?.role ?? 'Driver',
    status: isUserActive(user) ? 'Active' : 'Inactive',
    territory: user?.territory || 'N/A',
  }), [user]);

  // All fields show the admin-configured value from /users/me (migration 027);
  // any column that's still NULL renders 'N/A' so the UI never lies about
  // having data that isn't actually persisted.
  const personalInfo: PersonalInfo = {
    staffName: profile.staffName,
    employeeId: user?.employee_id || 'N/A',
    role: profile.role,
    assignedTerritory: user?.territory || 'N/A',
  };

  const contactInfo: ContactInfo = {
    email: user?.email ?? 'N/A',
    phone: user?.phone || 'N/A',
    department: user?.department || 'N/A',
  };

  const personalFields = [
    { label: 'Staff Name', value: personalInfo.staffName },
    { label: 'Employee ID', value: personalInfo.employeeId },
    { label: 'Role', value: personalInfo.role },
    { label: 'Assigned Territory', value: personalInfo.assignedTerritory },
  ];

  const contactFields = [
    { label: 'Email', value: contactInfo.email },
    { label: 'Phone', value: contactInfo.phone },
    { label: 'Department', value: contactInfo.department },
  ];

  return (
    <div className={styles.container}>
      <ProfileHeader profile={profile} />

      <div className={styles.infoGrid}>
        <InfoCard title="Personal Information" icon="personal" fields={personalFields} />
        <InfoCard title="Contact Information" icon="contact" fields={contactFields} />
      </div>

      <NotificationPreferences preferences={preferences} onToggle={handleTogglePreference} />
      <SecuritySettings options={defaultSecurityOptions} onOptionClick={handleSecurityClick} />
      <SettingsSection items={defaultSettingsItems} />
      <LogoutButton />

      <p className={styles.version}>VIN pulse OS v2.1.0</p>

      <ChangePasswordModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
};

export const DriverProfilePage: React.FC = () => {
  return <DriverProfile />;
};

export { DriverProfile };
export default DriverProfile;
